<template>
  <div class="gm-admin-tab">
    <server-table
      class="text-center groovemail-block-domain-in-emails-table"
      ref="serverTable"
      :url="blockDomainsInEmailContentUrl"
      tableRef="blacklistDomainsInEmailTable"
      :slotData="blockDomainTableSlotData"
      :tableColumns="blocklistMailTableColumns"
      :tableOptions="blocklistMailTableOptions"
      tableTitle="frontend_groovemail_admin_block_domains"
      addNewBtnText="frontend_groovemail_admin_block_domains"
      @addNewData="addBlacklistedMail"
      @loaded="hideLoader()"
      @loading="showLoader()"
    >
    </server-table>

    <gs-named-modal
      id="blockDomainCreateModal"
      name="block-domain-modal"
      :title="modalTitle"
      @closed="blockedDomainsModalClosed()"
    >
      <div class="gm-admin-modal__body">
        <gs-field :label="$t('frontend_groovemail_admin_block_domains')">
          <text-input
            variant="base"
            v-model="model.domain"
            :validator="$v.model.domain"
            :placeholder="this.$t('frontend_groovemail_admin_block_domains_enter_domains')"
          />
        </gs-field>
        <p class="gm-admin-tab__note">
          <span class="fa fa-info-circle"></span>
          {{ this.$t('frontend_groovemail_admin_block_from_domains_note') }}
        </p>
        <gs-field :label="$t('frontend_groovemail_block_domains_custom_message_placeholder')">
          <text-area
            variant="base"
            v-model="model.blockedDomainCustomMessage"
            :placeholder="this.$t('frontend_groovemail_block_domains_custom_message_placeholder')"
            rows="5"
          />
        </gs-field>
        <div class="gm-admin-modal__footer">
          <gs-button variant="outline" @click="closeDomainBlacklistModal()">
            {{ $t('frontend_cancel') }}
          </gs-button>
          <gs-button
            variant="primary"
            @click="
              addModal ? addDomainsToBlacklist() : editDomainsToBlacklist()
            "
          >
            {{ addModal ? this.$t('frontend_save') : this.$t('frontend_update') }}
          </gs-button>
        </div>
      </div>
    </gs-named-modal>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-blacklisted`"
      :name="`confirmation-delete-blacklisted`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_blocked_domains_in_email_are_you_sure_delete_domains')"
      @confirm="deleteBlacklistedMail"
      @close="closeDeleteBlacklistedModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import { showRequestErrorAlert } from '@/components/showDialog.js';
import { postData, putData, deleteData } from '@/utils/apiHelper.js';
import { adminUrls } from '@/utils/urls.js';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import monthsMixin from '@/mixin/monthsMixin.js';


let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_block_domains_email_content_title'),
    };
  },
  mixins: [demoModeValuesMixin, monthsMixin],
  data() {
    return {
      currentDomain: '',
      modalType: 'add',
      confirmId: null,
      blockDomainsInEmailContentUrl:
        adminUrls.blocklistsUrls.blockDomainsInEmailContent,
      model: {
        domain: '',
        blockedDomainCustomMessage: '',
      },
      blocklistMailTableColumns: [
        'domain',
        'message',
        'created_at',
        'actions',
      ],
      blocklistMailTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        filterable: ['domain'],
        sortable: ['domain', 'created_at'],
        headings: {
          domain: () => this.$t('frontend_gduikit_sidebar_product_domains'),
          message: () =>
            this.$t('frontend_groovemail_block_domains_email_content_message'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.getDataActions(),
        },
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
               self.hideLoader();
            });
        },
        responseAdapter({ data }) {
          if(self.demoMode) {
            const newData = data[0]?.data.map((obj) => ({
              ...obj,
              domain: self.basicDomainText,
            }));
            
            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: data[0].data,
            count: data[0].total,
          };
        },
      },
    };
  },

  validations: {
    model: {
      domain: {
        required,
      },
    },
  },

  components: {
    ServerTable,
  },

  created() {
    self = this;
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    // Returns true if modalType is add.
    addModal() {
      return this.modalType === 'add';
    },

    // Returns modal title based on modal type
    modalTitle() {
      return this.addModal
        ? this.$t('frontend_groovemail_admin_block_domains')
        : this.$t('frontend_groovemail_block_domains_edit_domain');
    },

    /**
     * Returns details of columns for block domain table data
     * @returns {array} slot columns data for block domain table 
     */
    blockDomainTableSlotData() {
      return [
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.created_at
              ? `${new Date(props.row.created_at).getDate()} ${
                  this.months[new Date(props.row.created_at).getMonth()]
                }, ${new Date(props.row.created_at).getFullYear()}`
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => {
            return true;
          },
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_edit'),
              conditions: () => {
                return true;
              },
              callback: (props) => this.editBlacklistedMail(props.row),
            },
            {
              id: 1,
              name: () => this.$t('frontend_delete'),
              conditions: () => {
                return true;
              },
              callback: (props) => this.openDeleteBlacklistedModal(props.row.id),
            },
          ],
        },
      ];
    },
  },

  watch: {
    // Used to refresh black list table when demo mode changes
    demoMode() {
      this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.refresh();
    },
  },

  methods: {
    ...mapActions('loader', ['LOADING']),

    // show loader
    showLoader() {
      this.LOADING(true);
    },

    // hide loader
    hideLoader (){
      this.LOADING(false);
    },

    // Opens add/edit block domain modal
    openDomainBlacklistModal() {
      this.$root.$emit('modal-open', 'block-domain-modal');
    },

    // Closes add/edit block domain modal
    closeDomainBlacklistModal() {
      this.$root.$emit('modal-close', 'block-domain-modal');
    },

    // Clears data in the domains model
    blockedDomainsModalClosed() {
      this.model.domain = '';
      this.model.blockedDomainCustomMessage = '';
      this.$v.model.domain.$reset();
    },

    /**
     * Returns column heading for total records
     * @returns {string} column heading for total records
     */
    getDataActions() {
      return this.$refs &&
        this.$refs.serverTable &&
        this.$refs.serverTable.$refs.blacklistDomainsInEmailTable &&
        this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.count
        ? `${this.$t(
            'frontend_groovemail_admin_blocklists_total_records_label'
          )}: ${
            this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.count
          }`
        : `${this.$t(
            'frontend_groovemail_admin_blocklists_total_records_label'
          )}: 0`;
    },

    // Opens add blacklisted modal
    addBlacklistedMail() {
      this.modalType = 'add';
      this.openDomainBlacklistModal();
    },

    /**
     * Opens edit blacklisted modal
     * @params {object} domain - block domain in email
     */
    editBlacklistedMail(domain) {
      this.modalType = 'edit';
      this.currentDomain = domain.id;
      this.openDomainBlacklistModal();
      this.model.domain = domain.domain;
      if (domain.message_type !== 1) {
        this.model.blockedDomainCustomMessage = domain.message;
      }
    },

    // Edits blocked domains in email
    async editDomainsToBlacklist() {
      this.$v.$touch();
      const valid = !this.$v.$invalid;
      if (this.model.domain.trim() && valid) {
        try {
          await putData(`${this.blockDomainsInEmailContentUrl}/${this.currentDomain}`, {
              blacklistEmailDomain: this.model.domain.trim(),
              blacklistEmailCustomMessage:
              this.model.blockedDomainCustomMessage.trim(),
            }
          );
          this.closeDomainBlacklistModal();
          this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.refresh();
          this.model.domain = '';
          this.model.blockedDomainCustomMessage = '';
          this.$v.model.domain.$reset();
        } catch (error) {
          // show error alert
          showRequestErrorAlert(error?.response?.data?.error);
        }
      }
    },

    // Adds block domains in email
    async addDomainsToBlacklist() {
      this.$v.$touch();
      const valid = !this.$v.$invalid;
      if (this.model.domain.trim() && valid) {
        try {
          await postData(this.blockDomainsInEmailContentUrl, {
            blacklistEmailDomain: this.model.domain.trim(),
            blacklistEmailCustomMessage:
              this.model.blockedDomainCustomMessage.trim(),
          });
          this.closeDomainBlacklistModal();
          this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.refresh();
          this.model.domain = '';
          this.model.blockedDomainCustomMessage = '';
          this.$v.model.domain.$reset();

        } catch (error) {
          // show error alert
          showRequestErrorAlert(error?.response?.data?.error);
        }
      }
    },

    // open delete blacklisted modal
    async openDeleteBlacklistedModal(id) {
      // set the Confirm id
      this.confirmId = id;

      // open the modal
      this.$refs[`confirmation-delete-blacklisted`].open();
    },

    // close delete blacklisted modal
    closeDeleteBlacklistedModal() {
      // reset the Confirm id
      this.confirmId = null;
    },

    // delete blacklisted domain
    async deleteBlacklistedMail() {
      try {
        await deleteData(`${window.config.apiUrl}${this.blockDomainsInEmailContentUrl}/${this.confirmId}`);
        this.$refs.serverTable.$refs.blacklistDomainsInEmailTable.refresh();
      } catch (error) {
        // show error alert
        showRequestErrorAlert(error?.response?.data?.error);
      }
    },
  },
};
</script>



<style lang="scss">
.groovemail-block-domain-in-emails-table #gd-server-table-view {
  .VueTables__table th:last-child {
    text-align: right;
  }

  .VueTables__table th:last-child span {
    display: inline-block;
  }
}
</style>
